import { t } from 'i18next'
import React, { useState } from 'react'

import { Avatar, Flex, Link, Separator, Spacing, Text, Tooltip } from '@ui'
import { useRootDispatch } from '@app/store'
import { setModal } from '@app/ac'
import { routeUtil, useAccessRole } from '@app/util'
import { PERMISSIONS } from '@app/const'

export const Me = ({ me, isOrg, logOut, openEmployeeCard, isLoading, isPluginEnabled }) => {
  const dispatch = useRootDispatch()
  const { email, name } = me
  const [closeCount, setCloseCount] = useState(0)

  const { canRead, canWrite } = useAccessRole()

  const hasAccessToEmpCalendar = canRead(PERMISSIONS.EMP_CALENDAR)

  return (
    <Tooltip
      position={Tooltip.POSITIONS.RIGHT_BOTTOM}
      clickable
      closeDetection={closeCount}
      anchor={<Avatar profile={me} />}
    >
      <>
        <Spacing size={Spacing.SIZES.SIZE_20} />
        <Spacing size={Spacing.SIZES.SIZE_20} type={Spacing.TYPES.HORIZONTAL}>
          <Flex align={Flex.POSITION.CENTER}>
            <Avatar profile={me} />
            <Spacing size={Spacing.SIZES.SIZE_12} type={Spacing.TYPES.HORIZONTAL} />
            <Flex direction={Flex.DIRECTION.COLUMN}>
              <Text
                text={name}
                type={Text.TYPES.BODY_LARGE}
                weight={Text.WEIGHTS.BOLD}
                color={Text.COLORS.PRIMARY}
              />
              <Text
                text={email}
                type={Text.TYPES.BODY_MEDIUM}
                color={Text.COLORS.GRAY}
                weight={Text.WEIGHTS.SEMI_MEDIUM}
                ellipsis
              />
            </Flex>
          </Flex>
        </Spacing>
        <Spacing size={Spacing.SIZES.SIZE_16}>
          <Separator />
        </Spacing>

        <Spacing size={Spacing.SIZES.SIZE_20} type={Spacing.TYPES.HORIZONTAL}>
          <Link
            type={Link.TYPES.ON_CLICK}
            onClick={() => {
              openEmployeeCard()
              setCloseCount(closeCount + 1)
            }}
          >
            <Text text={t('SETTINGS_NT')} type={Text.TYPES.BODY_LARGE} />
          </Link>
          {hasAccessToEmpCalendar &&
            <>
              <Spacing size={Spacing.SIZES.SIZE_10} />
              <Link
                type={Link.TYPES.ON_CLICK}
                onClick={() => {
                  // There's no need to update route if we're already there
                  if (!window.location.pathname.includes('/my-timetable/')) {
                    routeUtil.navigate('/my-timetable')
                  }
                }}
              >
                <Text text={t('MY_EMP_CALENDAR_ME_NAV')} type={Text.TYPES.BODY_LARGE} />
              </Link>
            </>}
          {
            canWrite(PERMISSIONS.MNG_CALENDAR) && canRead(PERMISSIONS.EMP_ATTENDANCE) && !isOrg && isPluginEnabled('attendance') &&
              <>
                <Spacing size={Spacing.SIZES.SIZE_10} />
                <Link
                  type={Link.TYPES.ON_CLICK}
                  onClick={() => {
                    // There's no need to update route if we're already there
                    if (!window.location.pathname.includes('/my-attendance/')) {
                      routeUtil.navigate('/my-attendance')
                    }
                  }}
                >
                  <Text text={t('MY_ATTENDANCE')} type={Text.TYPES.BODY_LARGE} />
                </Link>
              </>
          }
        </Spacing>

        <Spacing size={Spacing.SIZES.SIZE_16}>
          <Separator />
        </Spacing>

        <Spacing size={Spacing.SIZES.SIZE_20} type={Spacing.TYPES.HORIZONTAL}>
          <Link
            type={Link.TYPES.ON_CLICK}
            onClick={async () => {
              await dispatch(setModal(null))
              await dispatch(setModal('reset-password'))
            }}
          >
            <Text text={t('PASSWORD_CHANGE_SCREEN_TITLE')} type={Text.TYPES.BODY_LARGE} />
          </Link>

          <Spacing size={Spacing.SIZES.SIZE_10} />

          <Link
            type={Link.TYPES.ON_CLICK}
            onClick={async () => {
              await dispatch(setModal(null))
              await dispatch(setModal('language-select'))
            }}
          >
            <Text text={t('ACC_SET_SELECT_LANGUAGE')} type={Text.TYPES.BODY_LARGE} />
          </Link>

          <Spacing size={Spacing.SIZES.SIZE_10} />

          <Link
            type={Link.TYPES.ON_CLICK}
            onClick={async () => {
              await dispatch(setModal(null))
              await dispatch(setModal('connect-calendar'))
            }}
          >
            <Text text={t('CONNECT_CALENDAR')} type={Text.TYPES.BODY_LARGE} />
          </Link>

          <Spacing size={Spacing.SIZES.SIZE_10} />

          <Link
            type={Link.TYPES.ON_CLICK}
            onClick={async () => {
              await dispatch(setModal(null))
              await dispatch(setModal('mobile-application'))
            }}
          >
            <Text text={t('EMPLOYEE_PROFILE_LAST_MOBILE')} type={Text.TYPES.BODY_LARGE} />
          </Link>

          <Spacing size={Spacing.SIZES.SIZE_10} />
          <Link
            type={Link.TYPES.ROUTER_LINK}
            to={isOrg ? '/organization/my-notification' : '/my-notification'}
          >
            <Text text={t('NOTIFICATION_SETT_TITLE')} type={Text.TYPES.BODY_LARGE} />
          </Link>
        </Spacing>

        <Spacing size={Spacing.SIZES.SIZE_16}>
          <Separator />
        </Spacing>

        <Spacing size={Spacing.SIZES.SIZE_20} type={Spacing.TYPES.HORIZONTAL}>
          <Flex direction={Flex.DIRECTION.COLUMN}>
            <Link type={Link.TYPES.A_TAG} to={t('LANDING_CONDITIONS_URL')}>
              <Text text={t('LEGAL')} type={Text.TYPES.BODY_LARGE} />
            </Link>
            <Spacing size={Spacing.SIZES.SIZE_10} />
            <Link type={Link.TYPES.A_TAG} to={t('LANDING_PRIVACY_URL')}>
              <Text text={t('GDPR')} type={Text.TYPES.BODY_LARGE} />
            </Link>
          </Flex>
        </Spacing>
        <Spacing size={Spacing.SIZES.SIZE_16}>
          <Separator />
        </Spacing>
        <Spacing size={Spacing.SIZES.SIZE_20} type={Spacing.TYPES.HORIZONTAL}>
          {!isLoading ? (
            <div
              onClick={
                () => logOut(isPluginEnabled('chat')) // we must enforce reload after logout if the 'chat' plugin is enabled, because the chat uses external library with its own websockets connection that needs to be reset like that
              }
              style={{ cursor: 'pointer' }}
            >
              <Text text={t('LOGOUT')} type={Text.TYPES.BODY_LARGE} />
            </div>
          ) : (
            <Text text={t('LOADING')} type={Text.TYPES.BODY_MEDIUM} color={Text.COLORS.GRAY} />
          )}
        </Spacing>
        <Spacing size={Spacing.SIZES.SIZE_20} />
      </>
    </Tooltip>
  )
}
